import { FONT_FAMILIES } from "@redotech/redo-model/email-builder";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import { forwardRef, useEffect, useLayoutEffect, useRef } from "react";

export const FormQuillEditor = forwardRef(function FormQuillEditor(
  {
    selector,
    toolbarElementId = "toolbar",
    readOnly,
    defaultValue,
    onTextChange,
    onSelectionChange,
    placeholder,
    disabled,
    setDisabled,
    setQuillRef,
  }: {
    selector: string;
    toolbarElementId?: string;
    readOnly?: boolean;
    defaultValue?: any;
    onTextChange?: (...args: any[]) => void;
    onSelectionChange?: (...args: any[]) => void;
    placeholder?: string;
    disabled?: boolean;
    setDisabled?: (disabled: boolean) => void;
    setQuillRef?: (quill: Quill) => void;
  },
  ref: any,
) {
  const containerRef = useRef(null);
  const defaultValueRef = useRef(defaultValue);
  const onTextChangeRef = useRef(onTextChange);
  const onSelectionChangeRef = useRef(onSelectionChange);

  useLayoutEffect(() => {
    onTextChangeRef.current = onTextChange;
    onSelectionChangeRef.current = onSelectionChange;
  });

  useEffect(() => {
    if (containerRef.current == null) {
      return;
    }
    const container = containerRef.current as HTMLElement;
    const Font: any = Quill.import("formats/font");
    Font.whitelist = FONT_FAMILIES;
    Quill.register(Font, true);

    const quill = new Quill("#" + selector, {
      placeholder: readOnly ? "" : placeholder || "",
      theme: "snow",
      modules: {
        toolbar: {
          container: "#" + toolbarElementId,
        },
        clipboard: {
          matchVisual: false,
        },
      },
      // Deliberately exclude image from formats
      formats: [
        "background",
        "bold",
        "color",
        "font",
        "code",
        "italic",
        "link",
        "size",
        "strike",
        "script",
        "underline",
        "blockquote",
        "header",
        "indent",
        "list",
        "align",
        "direction",
        "code-block",
      ],
    });

    if (ref) {
      if (typeof ref === "function") {
        ref(quill);
      } else {
        ref.current = quill;
      }
    }
    quill.enable(!readOnly);

    if (defaultValueRef.current) {
      quill.setContents(defaultValueRef.current);
    }

    quill.on(Quill.events.TEXT_CHANGE, (...args) => {
      onTextChangeRef.current?.(...args);
    });

    quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
      onSelectionChangeRef.current?.(...args);
    });

    quill.on(Quill.events.EDITOR_CHANGE, () => {
      if (setDisabled) {
        setDisabled(quill.getLength() <= 1);
      }
    });

    return () => {
      if (typeof ref === "function") {
        ref(null);
      } else if (ref) {
        ref.current = null;
      }
      container.innerHTML = "";
    };
  }, [ref, readOnly]);

  return <div ref={containerRef} />;
});
